import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import apiService from '../services/api';

interface Usuario {
  username: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: 'administrador' | 'operador';
  empresasAsignadas?: string[];
}

interface AuthContextType {
  usuario: Usuario | null;
  loading: boolean;
  isAdministrador: boolean;
  isOperador: boolean;
  empresasAsignadas: string[];
  refreshUsuario: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUsuario = async () => {
    try {
      const currentUser = await getCurrentUser();
      // El username en Cognito tiene el prefijo fact_, lo removemos
      const username = currentUser.username.replace('fact_', '');
      
      // Obtener datos completos del usuario desde DynamoDB
      const userData = await apiService.getUsuario(username);
      setUsuario(userData);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
      setUsuario(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsuario();
  }, []);

  const refreshUsuario = async () => {
    await loadUsuario();
  };

  const value: AuthContextType = {
    usuario,
    loading,
    isAdministrador: usuario?.rol === 'administrador',
    isOperador: usuario?.rol === 'operador',
    empresasAsignadas: usuario?.empresasAsignadas || [],
    refreshUsuario,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
